/**
 * What this one call cost, as a line under its transcript.
 *
 * The four token columns have been filling up on every call since the co-pilot
 * shipped and no screen has ever shown them. The report answers "where is the
 * month going"; this answers the question somebody asks while looking at one
 * conversation - whether THIS call is the expensive kind. A per-call figure is
 * also the only place a wrong assumption shows up early: a thirty-minute call
 * that cost eight cents of model time is a fact somebody notices, and a monthly
 * total that is quietly 30x too high is not.
 *
 * SILENT WHEN THERE IS NOTHING TO SAY. Not "0.00" and not a row of dashes: a
 * call with no AI and no price yet has nothing to report, and a line of zeroes
 * would read as a claim that it was free.
 */
import { useT } from "../../../../i18n/I18nProvider";
import type { CallDetail } from "../../../../lib/calls";
import { fmtMicros, fmtTokens } from "./money";

export function CallSpend({ call }: { call: CallDetail }) {
  const t = useT();

  const tokensIn = (call.transcriptTokensIn ?? 0) + (call.pilotTokensIn ?? 0);
  const tokensOut = (call.transcriptTokensOut ?? 0) + (call.pilotTokensOut ?? 0);
  const price = fmtMicros(call.priceMicros);

  const parts: string[] = [];
  // Twilio's charge first: it is the number that exists for every call, whereas
  // the AI columns are only filled on the ones the co-pilot or the transcriber
  // touched.
  if (price !== null) parts.push(`${price} ${call.priceUnit || "USD"}`);
  if (call.pilotWindows > 0) parts.push(t("calls.detail.windows", { n: call.pilotWindows }));
  if (tokensIn > 0 || tokensOut > 0) {
    parts.push(t("calls.cost.tokens", { in: fmtTokens(tokensIn), out: fmtTokens(tokensOut) }));
  }
  if (parts.length === 0) return null;

  return (
    <p className="mt-1 text-[11px] text-muted-foreground">
      {/* A middle dot rather than a table: three short facts on one line, and a
          grid for them would give the smallest thing on the pane the most
          structure of anything on it. */}
      {parts.join(" · ")}
    </p>
  );
}
